import 'angular'
import {find, uniq} from 'lodash-es';
import {RootScope} from "../../types/angular-types";
import {
    ApprovalAction,
    ApprovalActionType,
    ApprovalInfo,
    ApprovalResource,
    ApprovalService,
    ApprovalState,
    ApprovalStatus
} from "../approvals-types";
import AppConstants from "../../gorillaboy/AppConstants";
import {IApprovalUtils} from "../ApprovalUtils";
import {VersionMetadata} from "../../types/DocMetadata";
import {UserMetadata} from "../../types/Common";
import {NativeRenderingUtils} from "../../document-viewer/NativeRenderingUtils";

import './content_partial.html';
import './title_bar.html';
//Include the file in the build too!
import SignalProvider from "../../gorillaboy/SignalProvider";

const EMPTY_RESOURCE: ApprovalResource = {
    ResourceUri: "EMPTY",
    ResourceKind: "EMPTY",
    ResourceName: "EMPTY",
    Properties: []
};

interface IScope extends RootScope {
    // methods
    showPeoplePanel: () => boolean;
    showFilesPanel: () => boolean;
    showCommentsPanel: () => boolean;
    peoplePanelButtonClickedHandler: () => void;
    filesPanelButtonClickedHandler: () => void;
    commentsPanelButtonClickedHandler: () => void;
    getDocVersionMetadataFromApprovalResource: (resource: ApprovalResource) => ng.IPromise<VersionMetadata>;
    setApprovalRequestById: (approvalId: string) => ng.IPromise<ApprovalInfo>;
    isResourceEmpty: (resource: ApprovalResource) => boolean;
    showPDFViewer: (resource: ApprovalResource) => boolean;
    showFilePreviewNotSupportedMessage: (resource: ApprovalResource) => boolean;
    showFilePreviewFailMessage: (resource: ApprovalResource) => boolean;
    downloadResource: (resource: ApprovalResource) => ng.IPromise<any>;
    getDocumentAndVersionIds: (resourceuri: string) => {documentId: string, versionId: string};
    // properties
    viewModel: {
        currentResource: ApprovalResource;
        currentDocumentUrl: string;
    };
    
    sidebar: {
        selectedIndex: number;
        tabName: string;
        tabs: string[]
    }
    loading: boolean,
    layout: {
        view: string;
        partialContent: string;
        partialTitleBar: string;
        showLeftSidebar: boolean;
        hasRightSidebar: boolean;
        showRightSidebar: boolean;
        showUploadPane: boolean
    };

    // approvalRequest holds all the info about a request except action info. However, some data in it are not returned by the service.
    // updateApprovalRequest() is used to construct a valid approvalRequest that is used in this model.
    approvalRequest: ApprovalInfo;

    // approvalActions keeps a list of approvalActions. it is used in "comment panel", and used to determine if a user has approved/rejected
    // the approval request
    approvalActions: ApprovalAction[];

    approvalResourceMarker: string;

    // key: resourceUri, value: VersionMetadata
    approvalResourceMetadatas: Map<string,VersionMetadata>;
}

function ApprovalViewControllerFn($scope : IScope, $sce: ng.ISCEService, $routeParams: any, $rootScope: RootScope, $q: ng.IQService, $route: any,
        ApprovalService: ApprovalService, ApprovalUtils: IApprovalUtils, NativeRenderingUtils: NativeRenderingUtils, DocumentService : any, MessageManager: any, MessageProvider: any, UserCollectionService: any) {
    //////////////////////////////
    // INITIALIZATION
    //////////////////////////////
    $scope.layout = {
        view: AppConstants.LayoutViewTypes.APPROVALS_VIEW,
        partialContent: './approvals/approvalView/content_partial.html',
        partialTitleBar: './approvals/approvalView/title_bar.html',
        showLeftSidebar: !!$rootScope.showLeftSidebar,
        hasRightSidebar: true,
        showRightSidebar: true,
        showUploadPane: false
    };
    $scope.sidebar = {
        selectedIndex: 0,
        tabName: AppConstants.RightTabTypes.APPROVALS_PEOPLE_TAB,
        tabs: [AppConstants.RightTabTypes.APPROVALS_PEOPLE_TAB,
            AppConstants.RightTabTypes.APPROVALS_FILES_TAB,
            AppConstants.RightTabTypes.APPROVALS_COMMENTS_TAB]
    };

    $scope.viewModel = {
        currentDocumentUrl: "",
        currentResource: null
    };

    $scope.approvalResourceMetadatas = new Map<string, VersionMetadata>();

    setApprovalRequestById($route.current.params.approvalId).then(initSelectedResource);

    //////////////////////////////
    // METHODS
    //////////////////////////////
    $scope.showPeoplePanel = () => {
        return $scope.sidebar.tabName !== AppConstants.RightTabTypes.APPROVALS_PEOPLE_TAB;
    };

    $scope.showFilesPanel = () => {
        return $scope.sidebar.tabName !== AppConstants.RightTabTypes.APPROVALS_FILES_TAB;
    };

    $scope.showCommentsPanel = () => {
        return $scope.sidebar.tabName !== AppConstants.RightTabTypes.APPROVALS_COMMENTS_TAB;
    };

    $scope.showPDFViewer = (resource: ApprovalResource) => {
        if (isResourceValid(resource)) {
            if (getPDFPreviewStatus(resource) === AppConstants.DocumentStatus.DOCUMENT_STATUS_ACTIVE) {
                return !!$scope.viewModel.currentDocumentUrl;
            }
        }
        return false;
    };

    $scope.showNativeRenderer = (resource: ApprovalResource) => {
        if (isResourceValid(resource)) {
            if (getPDFPreviewStatus(resource) === AppConstants.DocumentStatus.DOCUMENT_STATUS_UNSUPPORTED && isNativeRenderingSupported(resource)) {
                return true;
            }
        }
        return false;
    };

    $scope.isResourceEmpty = (resource: ApprovalResource) => {
        return resource && resource.ResourceUri === EMPTY_RESOURCE.ResourceUri;
    };

    $scope.showFilePreviewNotSupportedMessage = (resource: ApprovalResource) => {
        if (isResourceValid(resource)) {
            if (getPDFPreviewStatus(resource) === AppConstants.DocumentStatus.DOCUMENT_STATUS_UNSUPPORTED && !isNativeRenderingSupported(resource)) {
                return true;
            }
        }
        return false;
    };

    $scope.showFilePreviewFailMessage = (resource: ApprovalResource) => {
        if (isResourceValid(resource)) {
            if (getPDFPreviewStatus(resource) === AppConstants.DocumentStatus.DOCUMENT_STATUS_FAILED) {
                return true;
            }
        }
        return false;
    };

    $scope.peoplePanelButtonClickedHandler = () => {
        $scope.$broadcast(SignalProvider.Sidebar.ShowRight, AppConstants.RightTabTypes.APPROVALS_PEOPLE_TAB);
    };

    $scope.filesPanelButtonClickedHandler = () => {
        $scope.$broadcast(SignalProvider.Sidebar.ShowRight, AppConstants.RightTabTypes.APPROVALS_FILES_TAB);
    };

    $scope.commentsPanelButtonClickedHandler = () => {
        $scope.$broadcast(SignalProvider.Sidebar.ShowRight, AppConstants.RightTabTypes.APPROVALS_COMMENTS_TAB);
    };

    $scope.downloadResource = (resource: ApprovalResource) => {
        if (!ApprovalUtils.isWorkDocsFile(resource.ResourceUri)) {
            return $q.reject('Cannot download resource because the given resource is not a workdocs file: ' + JSON.stringify(resource));
        }
        let documentAndVersionIdsPair = ApprovalUtils.getDocumentAndVersionIds($scope.viewModel.currentResource.ResourceUri);
        return DocumentService.getDocumentVersionSource(documentAndVersionIdsPair.documentId, documentAndVersionIdsPair.versionId, null, false).then((response) => {
            var sourceUrl = response.data.Metadata.Source.ORIGINAL;
            $scope.documentSourceUrl = $sce.trustAsResourceUrl(sourceUrl);
            return ;
        });
    };

    $scope.setApprovalRequestById = setApprovalRequestById;

    $scope.getDocumentAndVersionIds = ApprovalUtils.getDocumentAndVersionIds;

    //////////////////////////////
    // LISTENER AND WATCHERS
    //////////////////////////////

    $scope.$on(SignalProvider.Approval.view.updateApprovalRequest, (event) => {
        setApprovalRequestById($route.current.params.approvalId);
    });

    $scope.$watch('viewModel.currentResource.ResourceUri', function(newVal, oldVal) {
        if (newVal !== oldVal && newVal !== EMPTY_RESOURCE.ResourceUri) {
            getDocVersionMetadataFromApprovalResource($scope.viewModel.currentResource).then(updateCurrentDocumentUrl);
        }
    });

    //////////////////////////////
    // HELPER FUNCTIONS
    //////////////////////////////
    function setApprovalRequestById(approvalId: string): ng.IPromise<ApprovalInfo> {
        $scope.loading = true;
        return getApprovalById($route.current.params.approvalId).then((approvalRequest) => {
            computeAndSetLevelStatus(approvalRequest);
            $scope.approvalRequest = approvalRequest;
            return getApprovalActionsById($route.current.params.approvalId).then((approvalActions) => {
                $scope.approvalActions = approvalActions;
                compuateAndSetApproverStatus(approvalRequest, $scope.approvalActions);
                $scope.loading = false;

                // Notice that even fetchAndSetUserThumbnail is async function. It does not block the main process
                // of updating approval request becuase thumbnail is not a critical info. This makes the main process
                // sligtly faster.
                fetchAndSetUserThumbnail(approvalRequest);
                return approvalRequest;
            });
        });
    };

    function getApprovalById(approvalId: string): ng.IPromise<ApprovalInfo> {
        return $q((resolve, reject) => {
            let result: ApprovalInfo = null;
            // STEP 1: get approval
            ApprovalService.GetApproval(approvalId)
            .then((response) => {
                result = response.data.ApprovalInfo;
                if (ApprovalUtils.isDescriptionMarkdown(result.Description)) {
                    result.MarkdownDescription = (window as any).remarkableMd.render(result.Description.substring(3));
                }
                return ApprovalService.DescribeApprovalResources(result.Id, null, null);
            })
            // STEP 2: get approval resources
            .then((response) => {
                $scope.approvalResourceMarker = response.data.Marker;
                result.Resources = response.data.Resources;
                return ApprovalService.DescribeAllApprovers(approvalId, null);
            })
            // STEP 3: get approvers for each level
            .then((approvers) => {
                result.LevelConfigs.forEach((levelConfig) => {
                    levelConfig.approvers = approvers.filter((approver) => approver.Level === levelConfig.LevelNumber);
                })
                // finally, return the composed approvalInfo with approvers and resources
                resolve(result);
            });
        });
    }

    function getApprovalActionsById(approvalId: string): ng.IPromise<ApprovalAction[]> {
        return ApprovalService.DescribeAllApprovalActions(approvalId, null);
    }

    function computeAndSetLevelStatus(approvalRequest: ApprovalInfo): void {
        // There is no direct way to tell the status of a level.
        // The level status computation is based on some rules.

        // 1. Any level before the current level must be approved
        // 2. Any level after the current level must be queued
        approvalRequest.LevelConfigs.forEach((level) => {
            if (level.LevelNumber < approvalRequest.CurrentLevel) {
                level.status = ApprovalStatus.APPROVED
            } else if (level.LevelNumber > approvalRequest.CurrentLevel) {
                level.status = ApprovalStatus.QUEUED
            }
        });

        // 3. if the approval has approved or rejected status, the current level must be in the same status
        // 4. for all other cases, the current level is pending
        let currentLevelConfig = find(approvalRequest.LevelConfigs, (level) => level.LevelNumber === approvalRequest.CurrentLevel);
        if (approvalRequest.State === ApprovalState.APPROVED) {
            currentLevelConfig.status = ApprovalStatus.APPROVED;
        } else if (approvalRequest.State === ApprovalState.REJECTED) {
            currentLevelConfig.status = ApprovalStatus.REJECTED
        } else {
            currentLevelConfig.status = ApprovalStatus.PENDING;
        }
    }

    function compuateAndSetApproverStatus(approvalRequest: ApprovalInfo, actions: ApprovalAction[]) {
        // The service does not return the approver's approval status
        // The status is compuated by the given approval action list

        // The default status is pending
        approvalRequest.LevelConfigs.forEach((level) => {
            level.approvers.forEach((approver) => {
                approver.Status = ApprovalStatus.PENDING;
            });
        });

        // Based on the given action list, change approvers' status
        actions.forEach((action) => {
            let currentLevelConfig = find(approvalRequest.LevelConfigs, (level) => level.LevelNumber === action.LevelNumber);
            if (currentLevelConfig) {
                let approver = find(currentLevelConfig.approvers, (approver) => approver.UserId === action.Approver.UserId);
                if (approver) {
                    if (action.Action === ApprovalActionType.APPROVE) {
                        approver.Status = ApprovalStatus.APPROVED;
                    } else if (action.Action === ApprovalActionType.REJECT) {
                        approver.Status = ApprovalStatus.REJECTED;
                    }
                } 
            }
        });
    }

    function fetchAndSetUserThumbnail(approvalRequest: ApprovalInfo): ng.IPromise<ApprovalInfo> {
        return $q((resolve, reject) => {
            let userIds: string[] = [];
            userIds = userIds.concat(approvalRequest.Owner.UserId);
            approvalRequest.LevelConfigs.forEach((level) => {
                userIds = userIds.concat(level.approvers.map((approver) => approver.UserId));
            });
            userIds = uniq(userIds);
            UserCollectionService.getUserCollection(userIds).then((response: UserMetadata[]) => {
                let ownerMetadata = find(response, (user) => user.Id === approvalRequest.Owner.UserId);
                if (ownerMetadata) {
                    approvalRequest.Owner.ProfilePhotoThumbnails = ownerMetadata.ProfilePhotoThumbnails;
                }
                
                approvalRequest.LevelConfigs.forEach((level) => {
                    level.approvers.forEach((approver) => {
                        let userMetadata = find(response, (user) => user.Id === approver.UserId);
                        if (userMetadata) {
                            approver.ProfilePhotoThumbnails = userMetadata.ProfilePhotoThumbnails;
                        }
                    });
                });
            });
        });
    }

    function initSelectedResource(approvalRequest: ApprovalInfo) {
        let firstFile = find($scope.approvalRequest.Resources, (resource) => ApprovalUtils.isWorkDocsFile(resource.ResourceUri));
        if (firstFile) {
            $scope.viewModel.currentResource = firstFile;
            return getDocVersionMetadataFromApprovalResource($scope.viewModel.currentResource).then(updateCurrentDocumentUrl);
        } else {
            $scope.viewModel.currentResource = EMPTY_RESOURCE;
            return $q.when();
        }
        
    }

    function getDocVersionMetadataFromApprovalResource(resource: ApprovalResource): ng.IPromise<VersionMetadata> {
        return $q(function(resolve, reject) {
            if ($scope.approvalResourceMetadatas.has(resource.ResourceUri)) {
                return resolve($scope.approvalResourceMetadatas.get(resource.ResourceUri));
            } else {
                let documentAndVersionIdsPair = ApprovalUtils.getDocumentAndVersionIds($scope.viewModel.currentResource.ResourceUri);
                DocumentService.getDocumentVersionPreview(documentAndVersionIdsPair.documentId, documentAndVersionIdsPair.versionId, null, false).then(function({data}) {
                    $scope.approvalResourceMetadatas.set(resource.ResourceUri, data.Metadata);
                    resolve(data.Metadata);
                }, function({error}) {
                    if (error && error.Message) {
                        if (error.Message.indexOf('EntityNotExistsException') >= 0) {
                            MessageManager.addErrorMessage(MessageProvider.documentDownloadFailNotExists);
                        } else if (error.Message.indexOf('UnauthorizedResourceAccessException') >= 0) {
                            MessageManager.addErrorMessage(MessageProvider.documentDownloadFailNoPermission);
                        }
                    } else {
                        MessageManager.addErrorMessage(MessageProvider.documentAccessFailDefault);
                    }
                    reject(error);
                });
            }
        });
    }

    function updateCurrentDocumentUrl(docVersion: VersionMetadata) {
        $scope.viewModel.currentDocumentUrl = AppConstants.PDF_VIEWER_BASE_URL +
                    "/viewer.html?file=" + encodeURIComponent(docVersion.Preview.PREVIEW);
    }

    function isResourceValid(resource: ApprovalResource): boolean {
        //========== not in unset state ======= AND ====== not empty entry ====
        return !!resource && !$scope.isResourceEmpty(resource);
    }

    function getPDFPreviewStatus (resource: ApprovalResource): string {
        if (isResourceValid(resource)) {
            if ($scope.approvalResourceMetadatas.has(resource.ResourceUri)) {
                return $scope.approvalResourceMetadatas.get(resource.ResourceUri).PdfPreviewStatus;
            }
        }
        return null;
    }

    function isNativeRenderingSupported(resource: ApprovalResource): boolean {
        if (resource && $scope.approvalResourceMetadatas.has(resource.ResourceUri)) {
            let versionMetadata = $scope.approvalResourceMetadatas.get(resource.ResourceUri);
            return NativeRenderingUtils.isNativeRenderingSupported(versionMetadata);
        }
        return false;
    }
}

ApprovalViewControllerFn.$inject = ['$scope', '$sce', '$routeParams', '$rootScope', '$q', '$route', 'ApprovalService', 'ApprovalUtils', 'NativeRenderingUtils',
    'DocumentService', 'MessageManager', 'MessageProvider', 'UserCollectionService'];
angular.module('GorillaBoy.Approvals')
.controller('ApprovalViewController', ApprovalViewControllerFn);


